import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Home, Mail, Smartphone, Loader, CheckCircle, BellOff } from 'lucide-react';

const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [prefs, setPrefs] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Missing unsubscribe token. Please use the link from your email.');
      setLoading(false);
      return;
    }
    fetchPrefs();
  }, [token]);

  const fetchPrefs = async () => {
    try {
      const res = await fetch(`/api/unsubscribe/preferences?token=${token}`);
      if (!res.ok) throw new Error('Invalid or expired link');
      const data = await res.json();
      setPrefs(data);
    } catch (err) {
      setError(err.message || 'Failed to load preferences');
    } finally {
      setLoading(false);
    }
  };

  const savePrefs = async (overrides = {}) => {
    setSaving(true);
    setSaved(false);
    try {
      const body = {
        email_digest_enabled: prefs.email_digest_enabled,
        sms_enabled: prefs.sms_enabled,
        sms_rate_alerts: prefs.sms_rate_alerts,
        sms_renewal_reminders: prefs.sms_renewal_reminders,
        ...overrides,
      };
      const res = await fetch(`/api/unsubscribe/update?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed to save');
      setPrefs(prev => ({ ...prev, ...body }));
      setSaved(true);
    } catch {
      setError('Failed to update preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const unsubscribeAll = () => {
    const all = {
      email_digest_enabled: false,
      sms_enabled: false,
      sms_rate_alerts: false,
      sms_renewal_reminders: false,
    };
    setPrefs(prev => ({ ...prev, ...all }));
    savePrefs(all);
  };

  const toggle = (key) => {
    setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
    setSaved(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error && !prefs) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="text-center max-w-md">
          <BellOff className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-red-400 mb-4">{error}</p>
          <Link to="/" className="text-primary hover:underline text-sm">Go to homepage</Link>
        </div>
      </div>
    );
  }

  const allOff = !prefs.email_digest_enabled && !prefs.sms_enabled && !prefs.sms_rate_alerts && !prefs.sms_renewal_reminders;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-background/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center">
          <Link to="/" className="flex items-center gap-2">
            <Home className="h-7 w-7 text-primary" />
            <span className="text-xl font-heading font-bold tracking-tight">
              My Mortgage <span className="text-primary">Canada</span>
            </span>
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-start justify-center pt-16 pb-16 px-6">
        <div className="w-full max-w-lg">
          <div className="text-center mb-8">
            <BellOff className="h-10 w-10 text-primary mx-auto mb-3" />
            <h1 className="text-3xl font-heading font-bold" data-testid="unsubscribe-title">
              Notification Preferences
            </h1>
            {prefs.email && (
              <p className="text-muted-foreground text-sm mt-1">
                Managing preferences for <span className="text-foreground">{prefs.email}</span>
              </p>
            )}
          </div>

          <div className="bg-card border border-border rounded-xl p-6 space-y-6">
            {/* Email section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Mail className="h-4 w-4 text-primary" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Email</h2>
              </div>
              <label className="flex items-center justify-between py-3 border-b border-border cursor-pointer" data-testid="toggle-email-digest">
                <div>
                  <p className="text-sm font-medium text-foreground">Weekly Digest</p>
                  <p className="text-xs text-muted-foreground">Rate trends, renewal timeline, and recommendations</p>
                </div>
                <button
                  onClick={() => toggle('email_digest_enabled')}
                  className={`relative w-11 h-6 rounded-full transition-colors ${prefs.email_digest_enabled ? 'bg-primary' : 'bg-muted'}`}
                >
                  <span className={`block w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${prefs.email_digest_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </label>
            </div>

            {/* SMS section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Smartphone className="h-4 w-4 text-primary" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">SMS</h2>
              </div>
              <label className="flex items-center justify-between py-3 border-b border-border cursor-pointer" data-testid="toggle-sms-enabled">
                <div>
                  <p className="text-sm font-medium text-foreground">SMS Notifications</p>
                  <p className="text-xs text-muted-foreground">Master toggle for all SMS messages</p>
                </div>
                <button
                  onClick={() => toggle('sms_enabled')}
                  className={`relative w-11 h-6 rounded-full transition-colors ${prefs.sms_enabled ? 'bg-primary' : 'bg-muted'}`}
                >
                  <span className={`block w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${prefs.sms_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </label>
              <label className="flex items-center justify-between py-3 border-b border-border cursor-pointer" data-testid="toggle-sms-rate-alerts">
                <div>
                  <p className="text-sm font-medium text-foreground">Rate Alerts</p>
                  <p className="text-xs text-muted-foreground">Notified when mortgage rates hit your target</p>
                </div>
                <button
                  onClick={() => toggle('sms_rate_alerts')}
                  disabled={!prefs.sms_enabled}
                  className={`relative w-11 h-6 rounded-full transition-colors ${prefs.sms_rate_alerts && prefs.sms_enabled ? 'bg-primary' : 'bg-muted'} ${!prefs.sms_enabled ? 'opacity-50' : ''}`}
                >
                  <span className={`block w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${prefs.sms_rate_alerts && prefs.sms_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </label>
              <label className="flex items-center justify-between py-3 border-b border-border cursor-pointer" data-testid="toggle-sms-renewal">
                <div>
                  <p className="text-sm font-medium text-foreground">Renewal Reminders</p>
                  <p className="text-xs text-muted-foreground">Reminders as your renewal date approaches</p>
                </div>
                <button
                  onClick={() => toggle('sms_renewal_reminders')}
                  disabled={!prefs.sms_enabled}
                  className={`relative w-11 h-6 rounded-full transition-colors ${prefs.sms_renewal_reminders && prefs.sms_enabled ? 'bg-primary' : 'bg-muted'} ${!prefs.sms_enabled ? 'opacity-50' : ''}`}
                >
                  <span className={`block w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${prefs.sms_renewal_reminders && prefs.sms_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </label>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-3 pt-2">
              <button
                onClick={() => savePrefs()}
                disabled={saving}
                data-testid="save-preferences-btn"
                className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? <Loader className="h-4 w-4 animate-spin" /> : saved ? <CheckCircle className="h-4 w-4" /> : null}
                {saving ? 'Saving...' : saved ? 'Saved' : 'Save Preferences'}
              </button>

              {!allOff && (
                <button
                  onClick={unsubscribeAll}
                  disabled={saving}
                  data-testid="unsubscribe-all-btn"
                  className="w-full py-2.5 border border-border text-muted-foreground rounded-lg text-sm font-medium hover:text-red-400 hover:border-red-400/50 transition-colors disabled:opacity-50"
                >
                  Unsubscribe from all
                </button>
              )}

              {allOff && saved && (
                <p className="text-center text-sm text-muted-foreground">
                  You have been unsubscribed from all notifications.
                </p>
              )}
            </div>

            {error && prefs && (
              <p className="text-red-400 text-sm text-center">{error}</p>
            )}
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6">
            You can also manage these settings from your{' '}
            <Link to="/dashboard" className="text-primary hover:underline">dashboard</Link>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnsubscribePage;
